// ═══════════════════════════════════════════════════════════════
// SCHEMA VIEWER — Interactive database relationship diagram
// Path: src/components/settings/SchemaViewer.jsx
//
// Queries Supabase information_schema dynamically to show
// all tables, columns, foreign keys, and row counts.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Database,
  Table2,
  Key,
  Link2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Hash,
  Type,
  Calendar,
  ToggleLeft,
  List,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { Card } from "../ui/Card";
import { ActionButton } from "../ui/ActionButton";
import { supabase } from "../../lib/supabase";

// ── Domain color mapping ────────────────────────────────────
const DOMAIN_COLORS = {
  "Donor Management": "#3b82f6",
  "Financial": "#16a34a",
  "Engagement": "#f59e0b",
  "System": "#8b5cf6",
  "Shows": "#ec4899",
  "Unknown": "#6b7280",
};

const TABLE_DOMAINS = {
  members: "Donor Management",
  profiles: "Donor Management",
  directors: "Donor Management",
  gifts: "Financial",
  gift_batches: "Financial",
  funds: "Financial",
  contact_logs: "Engagement",
  chapter_events: "Engagement",
  event_attendance: "Engagement",
  pathways: "System",
  app_settings: "System",
  personas: "System",
  saved_comm_lists: "System",
  shows: "Shows",
  sponsorship_packages: "Shows",
  show_sponsorships: "Shows",
  benefit_templates: "Shows",
  sponsorship_benefit_fulfillments: "Shows",
};

const getDomain = (table) => TABLE_DOMAINS[table] || "Unknown";
const getDomainColor = (table) => DOMAIN_COLORS[getDomain(table)] || DOMAIN_COLORS.Unknown;

// ── Column type icon ────────────────────────────────────────
const TypeIcon = ({ dataType }) => {
  if (dataType.includes("uuid")) return <Key size={ICON_SIZES.xs} />;
  if (dataType.includes("int") || dataType.includes("numeric") || dataType.includes("real"))
    return <Hash size={ICON_SIZES.xs} />;
  if (dataType.includes("timestamp") || dataType.includes("date"))
    return <Calendar size={ICON_SIZES.xs} />;
  if (dataType.includes("bool")) return <ToggleLeft size={ICON_SIZES.xs} />;
  if (dataType.includes("json") || dataType.includes("ARRAY"))
    return <List size={ICON_SIZES.xs} />;
  return <Type size={ICON_SIZES.xs} />;
};

export const SchemaViewer = () => {
  const { palette, showMessage } = useApp();
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState({});
  const [foreignKeys, setForeignKeys] = useState([]);
  const [rowCounts, setRowCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedTable, setExpandedTable] = useState(null);
  const [viewMode, setViewMode] = useState("diagram"); // "diagram" or "list"

  // ── Load schema from information_schema ───────────────────
  const loadSchema = async () => {
    setLoading(true);
    try {
      // Get tables
      const { data: tableData, error: tableErr } = await supabase
        .rpc("get_schema_tables")
        .select();

      // Fallback: query tables directly if RPC doesn't exist
      let tableList;
      if (tableErr) {
        // Use a simpler approach — query known tables
        tableList = Object.keys(TABLE_DOMAINS);
      } else {
        tableList = (tableData || []).map((t) => t.table_name);
      }

      setTables(tableList);

      // Get columns for each table
      const colMap = {};
      for (const table of tableList) {
        try {
          // Get one row to discover columns
          const { data, error } = await supabase
            .from(table)
            .select("*")
            .limit(1);

          if (!error && data && data.length > 0) {
            colMap[table] = Object.keys(data[0]).map((col) => ({
              name: col,
              dataType: typeof data[0][col] === "number"
                ? "numeric"
                : typeof data[0][col] === "boolean"
                ? "boolean"
                : data[0][col] === null
                ? "unknown"
                : typeof data[0][col] === "object"
                ? Array.isArray(data[0][col])
                  ? "ARRAY"
                  : "jsonb"
                : data[0][col]?.match?.(/^\d{4}-\d{2}-\d{2}/)
                ? "timestamptz"
                : data[0][col]?.match?.(/^[0-9a-f]{8}-/)
                ? "uuid"
                : "text",
            }));
          } else if (!error && data && data.length === 0) {
            // Empty table — try to get columns from a dummy insert error
            colMap[table] = [];
          } else {
            colMap[table] = [];
          }
        } catch {
          colMap[table] = [];
        }
      }
      setColumns(colMap);

      // Get row counts
      const counts = {};
      for (const table of tableList) {
        try {
          const { count, error } = await supabase
            .from(table)
            .select("*", { count: "exact", head: true });
          if (!error) counts[table] = count;
        } catch {
          counts[table] = "?";
        }
      }
      setRowCounts(counts);

      // Detect foreign keys from column naming convention
      const fks = [];
      Object.entries(colMap).forEach(([table, cols]) => {
        cols.forEach((col) => {
          if (col.name === "id") return;
          if (col.name.endsWith("_id") && (col.dataType === "uuid" || col.dataType === "unknown")) {
            const targetTable = guessTargetTable(col.name, tableList);
            if (targetTable) {
              fks.push({
                from: table,
                fromCol: col.name,
                to: targetTable,
                toCol: "id",
              });
            }
          }
        });
      });
      setForeignKeys(fks);
    } catch (err) {
      showMessage("Failed to load schema: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchema();
  }, []);

  // ── Guess target table from FK column name ────────────────
  function guessTargetTable(colName, tableList) {
    const base = colName.replace(/_id$/, "");
    // Direct match
    if (tableList.includes(base)) return base;
    // Pluralize
    if (tableList.includes(base + "s")) return base + "s";
    // Known mappings
    const knownMaps = {
      member_id: "members",
      fund_id: "funds",
      batch_id: "gift_batches",
      event_id: "chapter_events",
      persona_id: "personas",
      profile_id: "profiles",
      created_by: "profiles",
      show_id: "shows",
      package_id: "sponsorship_packages",
      template_id: "benefit_templates",
      sponsorship_id: "show_sponsorships",
      director_id: "directors",
    };
    return knownMaps[colName] || null;
  }

  // ── Group tables by domain ────────────────────────────────
  const groupedTables = useMemo(() => {
    const groups = {};
    tables.forEach((t) => {
      const domain = getDomain(t);
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(t);
    });
    return groups;
  }, [tables]);

  // ── Styles ────────────────────────────────────────────────
  const th = {
    padding: "6px 10px",
    textAlign: "left",
    fontSize: "0.6rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: palette.text3,
    borderBottom: `2px solid ${palette.cardBorder}`,
  };

  const td = {
    padding: "4px 10px",
    fontSize: FONT_SIZES.xs,
    fontFamily: FONT_FAMILY,
    borderBottom: `1px solid ${palette.cardBorder}`,
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: palette.textOnGrad, fontFamily: FONT_FAMILY }}>
        <RefreshCw size={ICON_SIZES.lg} style={{ marginBottom: 8, animation: "spin 1s linear infinite" }} />
        <div>Loading database schema...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Database size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
          <span style={{ fontSize: FONT_SIZES.md, fontWeight: 700, color: palette.textOnGrad, fontFamily: FONT_FAMILY }}>
            Database Schema
          </span>
          <span style={{ fontSize: FONT_SIZES.xs, color: palette.textOnGrad, opacity: 0.7, fontFamily: FONT_FAMILY }}>
            {tables.length} tables · {foreignKeys.length} relationships
          </span>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button
            onClick={() => setViewMode("diagram")}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
              fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
              background: viewMode === "diagram" ? palette.accent1 : `${palette.accent1}10`,
              color: viewMode === "diagram" ? "#fff" : palette.accent1,
            }}
          >
            Diagram
          </button>
          <button
            onClick={() => setViewMode("list")}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: "0.65rem", fontWeight: 600,
              fontFamily: FONT_FAMILY, cursor: "pointer", border: "none",
              background: viewMode === "list" ? palette.accent1 : `${palette.accent1}10`,
              color: viewMode === "list" ? "#fff" : palette.accent1,
            }}
          >
            List
          </button>
          <ActionButton label="Refresh" icon={RefreshCw} small variant="secondary" onClick={loadSchema} />
        </div>
      </div>

      {/* Domain legend */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {Object.entries(DOMAIN_COLORS).filter(([d]) => d !== "Unknown").map(([domain, color]) => (
          <div key={domain} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
            <span style={{ color: palette.textOnGrad }}>{domain}</span>
          </div>
        ))}
      </div>

      {viewMode === "diagram" ? (
        /* ═══ DIAGRAM VIEW ═══════════════════════════════════ */
        <div>
          {Object.entries(groupedTables).map(([domain, domainTables]) => (
            <div key={domain} style={{ marginBottom: 24 }}>
              <div style={{
                fontSize: FONT_SIZES.sm, fontWeight: 700, color: DOMAIN_COLORS[domain] || palette.textOnGrad,
                fontFamily: FONT_FAMILY, marginBottom: 10, display: "flex", alignItems: "center", gap: 6,
              }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: DOMAIN_COLORS[domain] }} />
                {domain}
              </div>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
              }}>
                {domainTables.map((table) => {
                  const domainColor = getDomainColor(table);
                  const isExpanded = expandedTable === table;
                  const tableCols = columns[table] || [];
                  const tableFKs = foreignKeys.filter((fk) => fk.from === table);
                  const referencedBy = foreignKeys.filter((fk) => fk.to === table);

                  return (
                    <Card
                      key={table}
                      style={{
                        padding: 0,
                        overflow: "hidden",
                        borderTop: `3px solid ${domainColor}`,
                        cursor: "pointer",
                      }}
                      onClick={() => setExpandedTable(isExpanded ? null : table)}
                    >
                      {/* Table header */}
                      <div style={{
                        padding: "10px 14px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        background: `${domainColor}08`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Table2 size={ICON_SIZES.sm} style={{ color: domainColor }} />
                          <span style={{
                            fontSize: FONT_SIZES.sm, fontWeight: 700,
                            color: palette.text1, fontFamily: FONT_FAMILY,
                          }}>
                            {table}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{
                            fontSize: "0.6rem", fontWeight: 600, color: palette.text3,
                            fontFamily: FONT_FAMILY,
                          }}>
                            {rowCounts[table]?.toLocaleString() ?? "?"} rows
                          </span>
                          {isExpanded
                            ? <ChevronDown size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
                            : <ChevronRight size={ICON_SIZES.sm} style={{ color: palette.text3 }} />}
                        </div>
                      </div>

                      {/* Relationship summary (always visible) */}
                      {(tableFKs.length > 0 || referencedBy.length > 0) && (
                        <div style={{ padding: "6px 14px", borderTop: `1px solid ${palette.cardBorder}` }}>
                          {tableFKs.map((fk) => (
                            <div key={fk.fromCol} style={{
                              fontSize: "0.6rem", color: palette.text2, fontFamily: FONT_FAMILY,
                              display: "flex", alignItems: "center", gap: 4, padding: "2px 0",
                            }}>
                              <Link2 size={ICON_SIZES.xs} style={{ color: domainColor }} />
                              <span style={{ color: palette.text3 }}>{fk.fromCol}</span>
                              <span style={{ color: palette.text3 }}>→</span>
                              <span style={{ fontWeight: 600, color: getDomainColor(fk.to) }}>
                                {fk.to}
                              </span>
                            </div>
                          ))}
                          {referencedBy.map((fk) => (
                            <div key={`${fk.from}.${fk.fromCol}`} style={{
                              fontSize: "0.6rem", color: palette.text2, fontFamily: FONT_FAMILY,
                              display: "flex", alignItems: "center", gap: 4, padding: "2px 0",
                            }}>
                              <Link2 size={ICON_SIZES.xs} style={{ color: palette.text3, transform: "rotate(180deg)" }} />
                              <span style={{ fontWeight: 600, color: getDomainColor(fk.from) }}>
                                {fk.from}
                              </span>
                              <span style={{ color: palette.text3 }}>← {fk.fromCol}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Expanded: column list */}
                      {isExpanded && tableCols.length > 0 && (
                        <div style={{
                          borderTop: `1px solid ${palette.cardBorder}`,
                          maxHeight: 300,
                          overflowY: "auto",
                        }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
                            <thead>
                              <tr>
                                <th style={{ ...th, paddingLeft: 14 }}>Column</th>
                                <th style={th}>Type</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tableCols.map((col) => {
                                const isFK = tableFKs.some((fk) => fk.fromCol === col.name);
                                const isPK = col.name === "id";
                                return (
                                  <tr key={col.name}>
                                    <td style={{
                                      ...td, paddingLeft: 14,
                                      fontWeight: isPK || isFK ? 600 : 400,
                                      color: isPK ? domainColor : isFK ? getDomainColor(
                                        (tableFKs.find((fk) => fk.fromCol === col.name) || {}).to
                                      ) : palette.text1,
                                    }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        {isPK && <Key size={ICON_SIZES.xs} style={{ color: domainColor }} />}
                                        {isFK && <Link2 size={ICON_SIZES.xs} style={{ color: palette.text3 }} />}
                                        {col.name}
                                      </div>
                                    </td>
                                    <td style={{ ...td, color: palette.text3 }}>
                                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <TypeIcon dataType={col.dataType} />
                                        {col.dataType}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {isExpanded && tableCols.length === 0 && (
                        <div style={{
                          padding: "12px 14px", textAlign: "center",
                          color: palette.text3, fontSize: FONT_SIZES.xs,
                          borderTop: `1px solid ${palette.cardBorder}`,
                        }}>
                          Empty table — no columns detected
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ═══ LIST VIEW ══════════════════════════════════════ */
        <div>
          {/* Relationships table */}
          <Card style={{ marginBottom: 16 }}>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <Link2 size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              Relationships ({foreignKeys.length})
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  <th style={th}>From Table</th>
                  <th style={th}>Column</th>
                  <th style={th}>→</th>
                  <th style={th}>To Table</th>
                </tr>
              </thead>
              <tbody>
                {foreignKeys.map((fk, idx) => (
                  <tr key={idx}>
                    <td style={{ ...td, fontWeight: 600, color: getDomainColor(fk.from) }}>{fk.from}</td>
                    <td style={{ ...td, color: palette.text2 }}>{fk.fromCol}</td>
                    <td style={{ ...td, color: palette.text3, textAlign: "center" }}>→</td>
                    <td style={{ ...td, fontWeight: 600, color: getDomainColor(fk.to) }}>{fk.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* All tables with row counts */}
          <Card>
            <div style={{
              fontSize: FONT_SIZES.sm, fontWeight: 700, color: palette.text1,
              fontFamily: FONT_FAMILY, marginBottom: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <Table2 size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
              All Tables ({tables.length})
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: FONT_FAMILY }}>
              <thead>
                <tr>
                  <th style={th}>Table</th>
                  <th style={th}>Domain</th>
                  <th style={{ ...th, textAlign: "right" }}>Rows</th>
                  <th style={{ ...th, textAlign: "right" }}>Columns</th>
                  <th style={{ ...th, textAlign: "right" }}>Foreign Keys</th>
                  <th style={{ ...th, textAlign: "right" }}>Referenced By</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((table) => {
                  const domainColor = getDomainColor(table);
                  const fkCount = foreignKeys.filter((fk) => fk.from === table).length;
                  const refCount = foreignKeys.filter((fk) => fk.to === table).length;
                  return (
                    <tr key={table}>
                      <td style={{ ...td, fontWeight: 600, color: palette.text1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: domainColor }} />
                          {table}
                        </div>
                      </td>
                      <td style={{ ...td, color: domainColor, fontWeight: 600, fontSize: "0.65rem" }}>
                        {getDomain(table)}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontWeight: 600, color: palette.text1 }}>
                        {rowCounts[table]?.toLocaleString() ?? "?"}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: palette.text2 }}>
                        {(columns[table] || []).length}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: fkCount > 0 ? palette.accent1 : palette.text3 }}>
                        {fkCount}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: refCount > 0 ? "#16a34a" : palette.text3 }}>
                        {refCount}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
};
